import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Project } from '../../types/entities';

interface ExportProjectDialogProps {
   isOpen: boolean;
   project: Project | undefined;
   counts: {
      scenes: number;
      characters: number;
      locations: number;
      plots: number;
   };
   isExporting: boolean;
   onClose: () => void;
   onConfirm: () => void;
}

function ExportProjectDialog({
   isOpen,
   project,
   counts,
   isExporting,
   onClose,
   onConfirm,
}: ExportProjectDialogProps) {
   const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
   const projectTitle = project?.title || 'Untitled Project';
   const fileName = `${project?.title || 'project'}.quillproject`;

   useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape' && !isExporting) {
            onClose();
         }
      };

      document.addEventListener('keydown', handleKeyDown);
      cancelButtonRef.current?.focus();

      return () => document.removeEventListener('keydown', handleKeyDown);
   }, [isExporting, isOpen, onClose]);

   return (
      <AnimatePresence>
         {isOpen && project && (
            <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
               <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={isExporting ? undefined : onClose}
                  className="absolute inset-0 bg-black/70 backdrop-blur-sm"
               />

               <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="export-project-title"
                  aria-describedby="export-project-description"
                  className="relative z-10 w-full max-w-lg p-6 bg-midnight border border-charcoal rounded-lg shadow-2xl"
               >
                  <div className="flex items-start justify-between gap-4 mb-6">
                     <div>
                        <h2
                           id="export-project-title"
                           className="text-2xl font-display text-pearl"
                        >
                           EXPORT PROJECT
                        </h2>
                        <p
                           id="export-project-description"
                           className="mt-1 text-xs font-mono text-ash"
                        >
                           Choose a user-accessible destination for this project file.
                        </p>
                     </div>
                     <button
                        type="button"
                        onClick={onClose}
                        disabled={isExporting}
                        aria-label="Close export dialog"
                        className="p-2 hover:bg-charcoal rounded transition-colors disabled:opacity-50"
                     >
                        <svg
                           className="w-5 h-5 text-silver"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                           />
                        </svg>
                     </button>
                  </div>

                  <div className="space-y-4">
                     <div className="rounded border border-charcoal bg-charcoal/30 p-4">
                        <p className="text-xs font-mono text-ash uppercase tracking-wider">
                           Project
                        </p>
                        <p className="mt-1 text-base font-mono text-pearl">{projectTitle}</p>
                        {project.genre && (
                           <p className="mt-1 text-xs font-mono text-silver">{project.genre}</p>
                        )}
                        {project.description && (
                           <p className="mt-3 text-xs leading-relaxed font-mono text-silver">
                              {project.description}
                           </p>
                        )}
                     </div>

                     <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {Object.entries(counts).map(([label, count]) => (
                           <div
                              key={label}
                              className="rounded border border-charcoal bg-charcoal/20 px-3 py-3"
                           >
                              <p className="text-lg font-display text-pearl">{count}</p>
                              <p className="text-[10px] font-mono text-ash uppercase">{label}</p>
                           </div>
                        ))}
                     </div>

                     <div className="rounded border border-slate/50 px-4 py-3">
                        <div className="flex justify-between gap-4 text-xs font-mono">
                           <span className="text-ash">File</span>
                           <span className="text-silver text-right break-all">{fileName}</span>
                        </div>
                        <div className="flex justify-between gap-4 mt-2 text-xs font-mono">
                           <span className="text-ash">Format</span>
                           <span className="text-silver">Quill Studio Project</span>
                        </div>
                     </div>

                     <p className="text-[11px] leading-relaxed font-mono text-ash">
                        The project file includes project details, scenes, characters, locations,
                        plots, notes, and embedded character or location images.
                     </p>
                  </div>

                  <div className="flex justify-end gap-3 pt-6">
                     <button
                        ref={cancelButtonRef}
                        type="button"
                        onClick={onClose}
                        disabled={isExporting}
                        className="px-6 py-2 bg-charcoal text-silver font-display text-lg rounded hover:bg-slate transition-colors disabled:opacity-50"
                     >
                        CANCEL
                     </button>
                     <motion.button
                        type="button"
                        onClick={onConfirm}
                        disabled={isExporting}
                        className="px-6 py-2 bg-amethyst hover:bg-rose text-white font-display text-lg rounded shadow-lg transition-all duration-200 disabled:opacity-50"
                     >
                        {isExporting ? 'EXPORTING...' : 'EXPORT PROJECT'}
                     </motion.button>
                  </div>
               </motion.div>
            </div>
         )}
      </AnimatePresence>
   );
}

export default ExportProjectDialog;
